import React, { useState, useEffect } from 'react';
import { Crown, Loader, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import { getUserSubscription, getUserOrders, isUserPremium } from '../lib/stripe';
import { useUser } from '../contexts/UserContext';
import { checkSupabaseConnection } from '../lib/supabase';

interface PremiumBadgeProps {
  className?: string;
  showLabel?: boolean;
}

const PremiumBadge: React.FC<PremiumBadgeProps> = ({ className = '', showLabel = true }) => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const checkPremiumStatus = async () => {
      try {
        setLoading(true);
        setError(null);
        setConnectionError(false);
        
        // Check Supabase connection first with timeout
        const connectionPromise = checkSupabaseConnection(2, 1500);
        const timeoutPromise = new Promise<boolean>((_, reject) => {
          setTimeout(() => reject(new Error('Connection check timeout')), 8000);
        });

        let isConnected = false;
        try {
          isConnected = await Promise.race([connectionPromise, timeoutPromise]);
        } catch (err: any) {
          console.warn('Connection check failed:', err);
          if (err.message === 'Failed to fetch' || err.name === 'TypeError' || err.message.includes('timeout')) {
            setConnectionError(true);
            return;
          }
          throw err;
        }
        
        if (!isConnected) {
          setConnectionError(true);
          return;
        }
        
        // Fetch subscription and orders data with retry logic
        const subscription = await getUserSubscription();
        const orders = await getUserOrders();
        
        // Check if user has premium status
        setIsPremium(isUserPremium(subscription, orders));
        setRetryCount(0); // Reset retry count on success
      } catch (error: any) {
        console.error('Error checking premium status:', error);
        
        // Handle network errors specifically
        if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
          setConnectionError(true);
        } else {
          setError(error instanceof Error ? error : new Error('Unknown error'));
        }
        
        // Set to false on error to be safe
        setIsPremium(false);
      } finally {
        setLoading(false);
      }
    };

    checkPremiumStatus();
  }, [user, retryCount]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  if (connectionError) {
    return (
      <div className={`inline-flex items-center space-x-1 text-red-500 ${className}`}>
        <WifiOff className="h-4 w-4" />
        {showLabel && (
          <button 
            onClick={handleRetry}
            className="text-sm underline hover:no-underline"
            title="Click to retry connection"
          >
            Connection error
          </button>
        )}
      </div>
    );
  }

  if (error && !connectionError) {
    return (
      <div className={`inline-flex items-center space-x-1 text-orange-500 ${className}`}>
        <AlertCircle className="h-4 w-4" />
        {showLabel && (
          <button 
            onClick={handleRetry}
            className="text-sm underline hover:no-underline"
            title="Click to retry"
          >
            Error loading
          </button>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`inline-flex items-center space-x-1 ${className}`}>
        <Loader className="h-4 w-4 animate-spin text-yellow-500" />
        {showLabel && <span className="text-sm text-gray-500">Loading...</span>}
      </div>
    );
  }

  if (!isPremium) {
    return null;
  }

  return (
    <div className={`inline-flex items-center space-x-1 ${className}`}>
      <Crown className="h-4 w-4 text-yellow-500 fill-yellow-500" />
      {showLabel && <span className="text-sm font-medium text-yellow-700">Premium</span>}
    </div>
  );
};

export default PremiumBadge;